import {
  BUILTIN_METADATA_KEYS,
  type HMAccountsMetadata,
  type HMDocumentInfo,
  type HMQueryBlockFilterOptions,
  type HMQueryBlockInput,
  type HMQueryBlockItemSummary,
  type UnpackedHypermediaId,
} from '@seed-hypermedia/client/hm-types'
import {formattedDate, normalizeDate, unpackHmId, type AnyTimestamp} from '@shm/shared'
import {matchesQueryFilterEquality} from '@shm/shared/models/query-block-filter'

/** Primitive presentation types inferred for custom Query table attributes. */
export type QueryTableAttributeType = 'text' | 'number' | 'boolean' | 'date' | 'list'

/** A discovered or built-in Query table column. */
export type QueryTableColumn = {
  id: string
  label: string
  type: QueryTableAttributeType
  defaultVisible: boolean
}

/** One ephemeral client-side Query table filter. */
export type QueryTableFilter = NonNullable<NonNullable<HMQueryBlockInput['viewer']>['filters']>[number]

/** Contextual data used to resolve column values that depend on derived data. */
export type QueryTableValueContext = {
  accountsMetadata?: HMAccountsMetadata
  interactionSummaries?: Record<string, HMQueryBlockItemSummary>
  citationCounts?: Record<string, number>
}

/** Returns bare account references that have metadata in the current query payload. */
export function getQueryTableAccountIds(value: unknown, context?: QueryTableValueContext): UnpackedHypermediaId[] {
  const values = Array.isArray(value) ? value : [value]
  return values.flatMap((candidate) => {
    if (typeof candidate !== 'string') return []
    const id = unpackHmId(candidate)
    if (!id || id.path?.length) return []
    const account = context?.accountsMetadata?.[id.uid]
    return account ? [account.id] : []
  })
}

const RESERVED_METADATA_KEYS = BUILTIN_METADATA_KEYS

/** Infers one simple table type from the non-empty values of an attribute. */
export function inferAttributeType(values: unknown[]): QueryTableAttributeType {
  const present = values.filter((value) => value !== null && value !== undefined && value !== '')
  if (present.length === 0) return 'text'
  if (present.every((value) => typeof value === 'number')) return 'number'
  if (present.every((value) => typeof value === 'boolean')) return 'boolean'
  if (present.every(Array.isArray)) return 'list'
  if (
    present.every(
      (value) =>
        typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value) && !Number.isNaN(Date.parse(value)),
    )
  ) {
    return 'date'
  }
  return 'text'
}

/** Builds the stable core columns and discovered metadata columns for Query table rows. */
export function buildQueryTableColumns(
  items: HMDocumentInfo[] = [],
  filterOptions?: HMQueryBlockFilterOptions,
): QueryTableColumn[] {
  const coreColumns: QueryTableColumn[] = [
    {id: 'title', label: 'Name', type: 'text', defaultVisible: true},
    {id: 'space', label: 'Space', type: 'text', defaultVisible: false},
    {id: 'updated', label: 'Last Modified', type: 'date', defaultVisible: true},
    {id: 'children', label: 'Subdocuments', type: 'number', defaultVisible: true},
    {id: 'comments', label: 'Comments', type: 'number', defaultVisible: true},
    {id: 'citations', label: 'Backlinks', type: 'number', defaultVisible: true},
    {id: 'authors', label: 'Authors', type: 'list', defaultVisible: false},
    {id: 'created', label: 'Created', type: 'date', defaultVisible: false},
    {id: 'path', label: 'Path', type: 'text', defaultVisible: false},
  ]
  const metadataKeys = Array.from(
    new Set([
      ...items.flatMap((item) => Object.keys(item.metadata).filter((key) => !RESERVED_METADATA_KEYS.has(key))),
      ...Object.keys(filterOptions ?? {})
        .filter((id) => id.startsWith('metadata:'))
        .map((id) => id.slice('metadata:'.length)),
    ]),
  ).sort()

  return coreColumns.concat(
    metadataKeys.map((key) => ({
      id: `metadata:${key}`,
      label: key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^./, (character) => character.toUpperCase()),
      type: filterOptions?.[`metadata:${key}`]?.type ?? inferAttributeType(items.map((item) => item.metadata[key])),
      defaultVisible: false,
    })),
  )
}

/** Returns all predefined sort fields and the custom metadata fields currently visible in the table. */
export function getQuerySortColumns(
  columns: QueryTableColumn[],
  columnVisibility: Record<string, boolean>,
): QueryTableColumn[] {
  return columns.filter((column) => !column.id.startsWith('metadata:') || columnVisibility[column.id] === true)
}

/** Extracts the list of tag values for a document from its metadata. */
export function getDocumentTags(item: HMDocumentInfo): string[] {
  const raw = item.metadata.tags ?? item.metadata.importTags
  if (typeof raw === 'string' && raw) {
    return raw
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
  }
  if (Array.isArray(raw)) {
    return raw.map(String).filter(Boolean)
  }
  return []
}

/** Returns the value addressed by a stable Query table column ID. */
export function getQueryTableValue(item: HMDocumentInfo, columnId: string, context?: QueryTableValueContext): unknown {
  if (columnId.startsWith('metadata:')) return item.metadata[columnId.slice('metadata:'.length)]
  if (columnId === 'title') return item.metadata.name || item.path.at(-1) || 'Untitled'
  // The space a document belongs to is named by its root, which is the first breadcrumb.
  if (columnId === 'space') return item.breadcrumbs?.[0]?.name || item.id?.uid || ''
  if (columnId === 'tags') return getDocumentTags(item)
  if (columnId === 'comments') {
    const summaryId = item.id?.id ?? item.id
    return context?.interactionSummaries?.[summaryId]?.comments ?? item.activitySummary?.commentCount ?? 0
  }
  if (columnId === 'citations') {
    const summaryId = item.id?.id ?? item.id
    return context?.citationCounts?.[summaryId] ?? 0
  }
  if (columnId === 'updated') return item.updateTime
  if (columnId === 'created') return item.createTime
  if (columnId === 'authors') {
    const authors = Array.isArray(item.authors) ? item.authors : []
    return authors
      .map((uid) => context?.accountsMetadata?.[uid]?.metadata?.name || uid)
      .filter(Boolean)
      .join(', ')
  }
  if (columnId === 'path') return item.path.join('/')
  if (columnId === 'children') {
    const summaryId = item.id?.id ?? item.id
    return context?.interactionSummaries?.[summaryId]?.children ?? item.activitySummary?.childrenCount ?? 0
  }
  return undefined
}

/** Turns a raw table value into a plain string for display or text filtering. */
export function queryTableValueToString(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.map(queryTableValueToString).join(', ')
  if (isTimestampValue(value)) return formattedDate(value as AnyTimestamp)
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

function isTimestampValue(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('seconds' in value || value instanceof Date || (typeof value === 'string' && !Number.isNaN(Date.parse(value))))
  )
}

/** Returns a primitive that can be used to compare two rows for a given column. */
export function getQueryTableSortValue(
  item: HMDocumentInfo,
  columnId: string,
  context?: QueryTableValueContext,
): string | number {
  const value = getQueryTableValue(item, columnId, context)
  if (columnId === 'updated' || columnId === 'created') {
    const date = normalizeDate(value as AnyTimestamp)
    return date?.getTime() ?? 0
  }
  if (columnId === 'title' || columnId === 'tags' || columnId === 'authors') {
    return queryTableValueToString(value).toLocaleLowerCase()
  }
  if (columnId === 'children' || columnId === 'comments' || columnId === 'citations') {
    return Number(value) || 0
  }
  if (Array.isArray(value)) return queryTableValueToString(value).toLocaleLowerCase()
  if (typeof value === 'number') return value
  return queryTableValueToString(value).toLocaleLowerCase()
}

/** Tests the global search against every available value in a Query table row. */
export function queryTableItemMatchesSearch(
  item: HMDocumentInfo,
  search: string,
  descriptors: QueryTableColumn[],
  context?: QueryTableValueContext,
): boolean {
  const normalized = search.trim().toLocaleLowerCase()
  if (!normalized) return true
  const text = descriptors
    .map((column) => queryTableValueToString(getQueryTableValue(item, column.id, context)).toLocaleLowerCase())
    .concat(queryTableValueToString(item.metadata).toLocaleLowerCase())
    .join('\n')
  return text.includes(normalized)
}

export function getQueryTableColumnType(
  columnId: string,
  value: unknown,
  descriptor?: QueryTableColumn,
): QueryTableAttributeType {
  if (descriptor) return descriptor.type
  if (columnId === 'updated' || columnId === 'created') return 'date'
  if (['children', 'comments', 'citations'].includes(columnId)) return 'number'
  if (isTimestampValue(value)) return 'date'
  if (typeof value === 'number' || typeof value === 'bigint') return 'number'
  if (typeof value === 'boolean') return 'boolean'
  if (value !== null && value !== undefined && value !== '') return inferAttributeType([value])
  return 'text'
}

function toComparableValue(raw: unknown, type: QueryTableAttributeType) {
  if (type === 'number' || type === 'boolean') {
    const n = typeof raw === 'boolean' ? Number(raw) : Number(raw)
    if (Number.isFinite(n)) return {type: 'number' as const, value: n}
  }
  if (type === 'date') {
    const date = normalizeDate(raw as AnyTimestamp)
    if (date) return {type: 'number' as const, value: date.getTime()}
    const n = Number(raw)
    if (Number.isFinite(n)) return {type: 'number' as const, value: n}
  }
  return {type: 'string' as const, value: queryTableValueToString(raw).toLocaleLowerCase()}
}

/** Applies temporary Query table filters using AND semantics. */
export function filterQueryTableItems(
  items: HMDocumentInfo[],
  filters: QueryTableFilter[],
  context?: QueryTableValueContext,
  descriptors?: QueryTableColumn[],
): HMDocumentInfo[] {
  return items.filter((item) =>
    filters.every((filter) => {
      const value = getQueryTableValue(item, filter.columnId, context)
      if (!filter.value.trim()) return true
      if (filter.operator === 'equals' || filter.operator === 'notEquals') {
        const rawValue = filter.columnId === 'tags' ? item.metadata.tags ?? item.metadata.importTags : value
        const comparable =
          filter.columnId === 'created' || filter.columnId === 'updated'
            ? normalizeDate(rawValue as AnyTimestamp)?.toISOString()
            : filter.columnId === 'tags' && typeof rawValue === 'string'
              ? rawValue
                  .split(',')
                  .map((tag) => tag.trim())
                  .filter(Boolean)
              : rawValue
        const matches = matchesQueryFilterEquality(comparable, filter.value)
        return filter.operator === 'equals' ? matches : !matches
      }
      const needle = filter.value.toLocaleLowerCase()
      if (filter.operator === 'contains') {
        const text = queryTableValueToString(value).toLocaleLowerCase()
        return text.includes(needle)
      }
      const descriptor = descriptors?.find((d) => d.id === filter.columnId)
      const type = getQueryTableColumnType(filter.columnId, value, descriptor)
      const left = toComparableValue(value, type)
      const right = toComparableValue(filter.value, type)
      if (left.type === 'number' && right.type === 'number') {
        return filter.operator === 'greaterThan' ? left.value > right.value : left.value < right.value
      }
      return filter.operator === 'greaterThan'
        ? String(left.value) > String(right.value)
        : String(left.value) < String(right.value)
    }),
  )
}

/** Moves one Query table column by a relative offset. */
export function moveQueryTableColumn(order: string[], columnId: string, offset: -1 | 1): string[] {
  const from = order.indexOf(columnId)
  const to = from + offset
  if (from < 0 || to < 0 || to >= order.length) return order
  const next = [...order]
  ;[next[from], next[to]] = [next[to]!, next[from]!]
  return next
}
